import { useSitewideOptionData } from "lib/store/sitewideOption";
import { useUserData } from "lib/store/user";
import { wasSignedUp } from "components/inHouseAdCallout/calloutContainer/lib";
import { useCustomFields } from "lib/hooks/useCustomFields";
import { NewsletterVariants } from "lib/types/common";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { updateZephrUserProfile } from "lib/fetchers/zephr/customFields";
import { trackGTMEvent } from "lib/utils";
import Cookies from "js-cookie";
import { newsletterToPropMapping } from "lib/constants/shared";

const PRO_ONLY_NEWSLETTERS = ["pro", "scoop"];

const getCorrectSignupUrl = (
  isAuthorizedUser: boolean,
  newsletterType: string,
  isProUser: boolean
) => {
  switch (true) {
    case !isProUser && PRO_ONLY_NEWSLETTERS.includes(newsletterType):
      return `/sifted-pro`;
    case isAuthorizedUser:
      return undefined;
    default:
      return `/members/login?newsletter=${newsletterType}`;
  }
};

/**
 * Hook for managing newsletter list display and subscription actions with optimistic updates.
 *
 * This hook fetches the newsletter list configuration, determines subscription status for each
 * newsletter, and provides handlers for newsletter sign-up actions with instant UI feedback.
 *
 * **Optimistic Update Pattern:**
 * When a user subscribes to a newsletter, this hook:
 * 1. Immediately updates local state (via `customFields.updateNewsletter`)
 * 2. Sends the subscription request to Zephr backend
 * 3. Reverts the local state if the API call fails
 *
 * This provides instant visual feedback without waiting for the API, while maintaining
 * data consistency through automatic rollback on failure.
 *
 * @returns Object containing:
 * - `mapped`: Array of newsletter configurations with subscription status and signup URLs
 * - `signUpToNewsletter`: Async function to handle newsletter subscription with optimistic updates
 *
 * @example
 * ```tsx
 * const { mapped, signUpToNewsletter } = useMappedNewsletters();
 *
 * return (
 *   <NewsletterList>
 *     {mapped.map(newsletter => (
 *       <NewsletterCard
 *         key={newsletter.id}
 *         {...newsletter}
 *         onSignUp={() => signUpToNewsletter(newsletter.newsletterType, newsletter.title)}
 *       />
 *     ))}
 *   </NewsletterList>
 * );
 * ```
 */
export function useMappedNewsletters() {
  const { newslettersList } = useSitewideOptionData();
  const customFields = useCustomFields();
  const { newsletters } = customFields;
  const { isAuthorisedUser: isAuthorized, isProUser, userId } = useUserData();

  const loginCookie = Cookies.get("blaize_session");
  const { track } = useMixpanelTracking();

  /**
   * Subscribes a user to a newsletter with optimistic UI updates.
   *
   * This function implements the optimistic update pattern:
   * 1. Immediately updates local state (instant visual feedback)
   * 2. Sends subscription request to Zephr backend
   * 3. Reverts local state if the API call fails
   *
   * @param type - The newsletter type identifier (e.g., 'fintech', 'climatetech')
   * @param label - The label/title of the newsletter for tracking purposes
   *
   * Note: This function only executes for authenticated users (userId present).
   */
  const signUpToNewsletter = async (type, label) => {
    if (userId && loginCookie) {
      const newsletter = newsletterToPropMapping[type];

      // Optimistic update: Immediately reflect subscription in UI
      // This provides instant feedback while the API call is in flight
      customFields.updateNewsletter(newsletter, true);

      try {
        const data = await updateZephrUserProfile(
          {
            [newsletter]: true,
          },
          loginCookie
        );

        if (data && data.success) {
          track("Newsletter sign up", {
            Newsletter_id: type,
            "Click text": label,
          });

          trackGTMEvent({
            event: "newsletterSignUp",
            newsletter: newsletter,
          });
        } else {
          // Revert on failure - maintains consistency when backend update fails
          customFields.updateNewsletter(newsletter, false);
        }
      } catch (error) {
        // Revert on error - maintains consistency when API call throws
        customFields.updateNewsletter(newsletter, false);
        throw error;
      }
    }
  };
  const mapped = (newslettersList || []).map((newsletter) => {
    const isSignedUp = wasSignedUp(
      newsletters,
      newsletter.newsletterType as NewsletterVariants
    );
    const signUpUrl = getCorrectSignupUrl(
      isAuthorized,
      newsletter.newsletterType,
      isProUser
    );

    return {
      ...newsletter,
      isAuthorized,
      isSignedUp,
      signUpUrl,
      onAction: async () => {
        if (isAuthorized && !isSignedUp && !signUpUrl) {
          await signUpToNewsletter(newsletter.newsletterType, newsletter.title);
          return;
        }
        await track("Newsletter sign up", {
          newsletterId: newsletter.id,
          newsletterTitle: newsletter.title,
          newsletterType: newsletter.newsletterType,
        });
      },
    };
  });
  const featured = mapped.filter((newsletter) => newsletter.isFeatured);
  const [featuredNewsletter, secondaryFeaturedNewsletter] = featured;
  const newsletterList = mapped.filter((newsletter) => !newsletter.isFeatured);
  return { featuredNewsletter, secondaryFeaturedNewsletter, newsletterList };
}
